import i18next from 'i18next';

import {
  Locale,
  format,
  formatDistance,
  formatDuration,
  intervalToDuration,
  type Duration,
} from 'date-fns';
import {
  enGB,
  de,
  es,
  fr,
  nl,
  pl,
  ro,
  ru,
  cs,
  it,
  pt,
  uk,
  bg,
  hu,
  sk,
  tr,
  hr,
  sr,
  ko,
  fi,
  da,
  sv,
  nn,
} from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';

interface Locales {
  [key: string]: Locale;
}

interface FormatOptions {
  locale?: Locale;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  firstWeekContainsDate?: 1 | 4;
  useAdditionalWeekYearTokens?: boolean;
  useAdditionalDayOfYearTokens?: boolean;
}
interface FormatDistanceOptions {
  includeSeconds?: boolean;
  addSuffix?: boolean;
  locale?: Locale;
}

interface FormatDurationOptions {
  format?: (keyof Duration)[];
  zero?: boolean;
  delimiter?: string;
  locale?: Locale;
}

export const locales: Locales = {
  en: enGB,
  da: da,
  de: de,
  es: es,
  fr: fr,
  fi: fi,
  nl: nl,
  pl: pl,
  it: it,
  ko: ko,
  bg: bg,
  cs: cs,
  hu: hu,
  nn: nn,
  no: nn,
  pt: pt,
  ro: ro,
  sk: sk,
  sv: sv,
  ru: ru,
  tr: tr,
  hr: hr,
  sr: sr,
  uk: uk,
};

let configuredTimeZone: string | null | undefined;

export const setI18nTimeZone = (timeZone: string | null | undefined): void => {
  configuredTimeZone = timeZone;
};

export const i18nFormat = (
  date: Date | number,
  formatStr = 'Pp',
  options?: FormatOptions,
): string => {
  const formatOptions = {
    locale: locales[i18next?.language.substring(0, 2) || 'de'],
    weekStartsOn: 1 as const,
    ...options,
  };

  return configuredTimeZone
    ? formatInTimeZone(date, configuredTimeZone, formatStr, formatOptions)
    : format(date, formatStr, formatOptions);
};

export const i18nFormatInTimeZone = (
  date: Date | number,
  timeZone: string | null | undefined,
  formatStr = 'Pp',
  options?: FormatOptions,
): string => {
  if (!timeZone) return i18nFormat(date, formatStr, options);

  return formatInTimeZone(date, timeZone, formatStr, {
    locale: locales[i18next?.language.substring(0, 2) || 'de'],
    weekStartsOn: 1,
    ...options,
  });
};

export const i18nFormatTimeZone = (
  date: Date | number,
  clientTimezone: string | undefined,
  type?: 'DateTime' | 'Date' | 'Time',
): string => {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: clientTimezone || 'Europe/Berlin',
  };

  if (!type || type === 'DateTime') {
    options.day = '2-digit';
    options.month = '2-digit';
    options.year = 'numeric';
    options.hour = 'numeric';
    options.minute = 'numeric';
  }

  if (type === 'Date') {
    options.day = '2-digit';
    options.month = '2-digit';
    options.year = 'numeric';
  }

  if (type === 'Time') {
    options.hour = 'numeric';
    options.minute = 'numeric';
  }

  return new Date(date).toLocaleString(i18next?.language || 'de', options);
};

export const i18nFormatDistance = (
  date: Date | number,
  baseDate: Date | number,
  options?: FormatDistanceOptions,
): string => {
  return formatDistance(date, baseDate, {
    locale: locales[i18next?.language.substring(0, 2) || 'de'], // or global.__localeId__
    ...options,
  });
};

export const i18nFormatDistanceToNow = (
  date: Date | number,
  options?: FormatDistanceOptions,
): string => {
  return formatDistance(date, new Date(), {
    locale: locales[i18next?.language.substring(0, 2) || 'de'], // or global.__localeId__
    ...options,
  });
};

export const i18nFormatDuration = (
  duration: Duration,
  options?: FormatDurationOptions,
): string => {
  return formatDuration(duration, {
    locale: locales[i18next?.language || 'de'], // or global.__localeId__
    ...options,
  });
};

export const i18nFormatDurationToNow = (
  date: Date | number,
  options?: FormatDurationOptions,
): string => {
  const duration = intervalToDuration({ start: date, end: new Date() });
  return formatDuration(duration, {
    locale: locales[i18next?.language.substring(0, 2) || 'de'], // or global.__localeId__
    ...options,
  });
};
